// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Shared WebSocket connection management utilities.
 *
 * Extracts common patterns from useWebSocket and useSpectatorWebSocket
 * to reduce duplication (~150 LOC shared between the hooks).
 */

import { useRef, useCallback } from 'react';
import { WS_HEARTBEAT_INTERVAL, WS_MAX_MISSED_PONGS } from '../constants';
import { logger } from './logger';
import { ClientActionEnum, ServerActionEnum } from '../types/game';

// Connection status constants (shared across all WebSocket hooks)
export const ConnectionStatus = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
} as const;
export type ConnectionStatusType = (typeof ConnectionStatus)[keyof typeof ConnectionStatus];

// Heartbeat configuration (sourced from centralized constants)
export const HEARTBEAT_INTERVAL = WS_HEARTBEAT_INTERVAL;
export const MAX_MISSED_PONGS = WS_MAX_MISSED_PONGS;

/**
 * Base refs and state management for WebSocket connections.
 * Call this at the start of your WebSocket hook to get initialized refs.
 */
export function useWebSocketRefs() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const reconnectAttempts = useRef(0);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const missedPongsRef = useRef(0);
  const connectRef = useRef<() => void>(() => {});
  const pendingPingsRef = useRef<Map<string, number>>(new Map());

  return {
    wsRef,
    reconnectTimeout,
    reconnectAttempts,
    heartbeatIntervalRef,
    missedPongsRef,
    connectRef,
    pendingPingsRef,
  };
}

/**
 * Heartbeat management for WebSocket connections.
 * Handles PING/PONG to detect stale connections and measure latency.
 */
export function useHeartbeat(
  wsRef: React.MutableRefObject<WebSocket | null>,
  heartbeatIntervalRef: React.MutableRefObject<ReturnType<typeof setInterval> | undefined>,
  missedPongsRef: React.MutableRefObject<number>,
  pendingPingsRef: React.MutableRefObject<Map<string, number>>,
  sendMessage: (action: string) => void,
  onRttMeasured?: (rtt: number) => void
) {
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = undefined;
    }
  }, [heartbeatIntervalRef]);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    missedPongsRef.current = 0;
    pendingPingsRef.current.clear();
    heartbeatIntervalRef.current = setInterval(() => {
      if (missedPongsRef.current >= MAX_MISSED_PONGS) {
        logger.warn('websocket_heartbeat_missed_pongs', {
          count: missedPongsRef.current,
        });
        wsRef.current?.close();
        return;
      }
      // Record timestamp before sending PING
      const now = performance.now();
      pendingPingsRef.current.set('last', now);
      sendMessage(ClientActionEnum.PING);
      missedPongsRef.current++;
    }, HEARTBEAT_INTERVAL);
  }, [wsRef, pendingPingsRef, sendMessage, stopHeartbeat, heartbeatIntervalRef, missedPongsRef]);

  const handlePong = useCallback(() => {
    missedPongsRef.current = 0;

    // Calculate RTT if we have a pending PING
    const pingTime = pendingPingsRef.current.get('last');
    if (pingTime && onRttMeasured) {
      const rtt = performance.now() - pingTime;
      onRttMeasured(rtt);
      pendingPingsRef.current.delete('last');
    }
  }, [pendingPingsRef, onRttMeasured, missedPongsRef]);

  return { startHeartbeat, stopHeartbeat, handlePong };
}

/**
 * Check if a message is a PONG response.
 */
export function isPongMessage(message: { action?: string }): boolean {
  return message.action === ServerActionEnum.PONG;
}

/**
 * Check if WebSocket is ready to connect (not already open/connecting).
 */
export function canConnect(wsRef: React.MutableRefObject<WebSocket | null>): boolean {
  return (
    wsRef.current?.readyState !== WebSocket.OPEN &&
    wsRef.current?.readyState !== WebSocket.CONNECTING
  );
}

/**
 * Build WebSocket URL for the current environment.
 *
 * In E2E testing with Vite dev server on port 1333:
 * - Vite proxy for WebSocket connections is unreliable
 * - Connect directly to backend on port 1337 instead of going through proxy
 * - This ensures consistent WebSocket connectivity in headless Playwright tests
 */
export function buildWebSocketUrl(path: string): string {
  // Use environment variable if set (for Cloudflare deployments)
  const envWsUrl = import.meta.env.VITE_WS_URL;
  if (envWsUrl) {
    return `${envWsUrl}${path}`;
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  // Check if running on Vite dev server (port 1333)
  // If so, connect directly to backend port 1337 to bypass unreliable proxy
  const port = window.location.port;
  const hostname = window.location.hostname;

  if (port === '1333') {
    // E2E testing: connect directly to backend port 1337
    // This bypasses the Vite proxy which is unreliable for WebSocket in headless mode
    return `${protocol}//${hostname}:1337${path}`;
  }

  // Production/normal dev: use window.location.host
  const wsHost = window.location.host;
  return `${protocol}//${wsHost}${path}`;
}

/**
 * Calculate exponential backoff delay with jitter for reconnection.
 * Jitter prevents thundering herd when multiple clients reconnect simultaneously.
 * Returns delay in milliseconds (50%-100% of base delay).
 */
export function getReconnectDelay(attempt: number, maxDelay: number = 30000): number {
  const baseDelay = Math.min(1000 * Math.pow(2, attempt), maxDelay);
  // Add jitter: 50%-100% of base delay to spread out reconnection attempts
  return Math.floor(baseDelay * (0.5 + Math.random() * 0.5));
}

/**
 * Clear any pending reconnection timeout.
 */
export function clearReconnectTimeout(
  reconnectTimeout: React.MutableRefObject<ReturnType<typeof setTimeout> | undefined>
) {
  if (reconnectTimeout.current) {
    clearTimeout(reconnectTimeout.current);
    reconnectTimeout.current = undefined;
  }
}
